// Embedder — all-MiniLM-L6-v2 via @huggingface/transformers.
// 22MB on the wire, cached in IndexedDB after first load.
// Embeds queries on demand; corpus is embedded once on first use (lazy).
//
// Honest note: the model genuinely runs locally in WebAssembly. No backend.
// First-load cost: ~3-5s on a fast connection (one-time). After that: ~80ms
// per query embedding. The corpus has ~60 chunks — embedding all of them
// happens in parallel on first use, takes ~1-2s total.

let pipelinePromise: Promise<unknown> | null = null;

async function getPipeline(): Promise<(text: string, opts: Record<string, unknown>) => Promise<{ data: Float32Array }>> {
  if (!pipelinePromise) {
    pipelinePromise = (async () => {
      const mod = await import("@huggingface/transformers");
      // Use the smallest, fastest English sentence transformer.
      return mod.pipeline("feature-extraction", "Xenova/all-MiniLM-L6-v2", {
        dtype: "fp32",
      });
    })();
  }
  // The TS types from transformers are awkward; we know what shape the
  // returned callable has.
  return pipelinePromise as Promise<(text: string, opts: Record<string, unknown>) => Promise<{ data: Float32Array }>>;
}

// Kick off model load (call this on page mount so the model is warm by the
// time the user types).
export function preloadEmbedder() {
  void getPipeline().catch(() => {
    // swallow; embed() will throw the same error when called
  });
}

export type EmbedderState = "idle" | "loading" | "ready" | "error";

let state: EmbedderState = "idle";
const listeners = new Set<(s: EmbedderState) => void>();

function setState(s: EmbedderState) {
  state = s;
  for (const fn of listeners) fn(s);
}

export function getEmbedderState(): EmbedderState {
  return state;
}

export function onEmbedderStateChange(fn: (s: EmbedderState) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function embed(text: string): Promise<Float32Array> {
  try {
    if (state === "idle") setState("loading");
    const pipe = await getPipeline();
    if (state !== "ready") setState("ready");
    const out = await pipe(text, { pooling: "mean", normalize: true });
    return out.data;
  } catch (err) {
    setState("error");
    throw err;
  }
}

// Cosine similarity between two unit-normalised vectors. Since we always
// pass normalize:true in the pipeline call, this is just dot product.
export function cosine(a: Float32Array, b: Float32Array): number {
  const n = Math.min(a.length, b.length);
  let sum = 0;
  for (let i = 0; i < n; i++) sum += a[i] * b[i];
  return sum;
}

// Embed an array of texts in series (faster than naive parallel for
// transformers.js because the runtime is single-threaded WASM).
export async function embedMany(texts: string[]): Promise<Float32Array[]> {
  const out: Float32Array[] = [];
  for (const t of texts) {
    out.push(await embed(t));
  }
  return out;
}
